import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

// Theme context interface
interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  /** Set the theme explicitly (used to apply a persisted per-user preference). */
  setDarkMode: (value: boolean) => void;
}

// Create context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

interface ThemeContextProviderProps {
  children: ReactNode;
}

/**
 * Theme provider — Tailwind-only. Tracks light/dark, persists the choice, and
 * toggles the `dark` class on <html> so Tailwind `dark:` variants and the
 * design-token CSS variables (defined in src/styles/tailwind.css) switch.
 */
export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  // Read theme from localStorage, defaulting to dark.
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : true;
  });

  const toggleTheme = useCallback(() => {
    setIsDarkMode((prev) => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  }, []);

  // Apply an explicit theme value (e.g. a per-user preference loaded from
  // Firestore at login). Persists to localStorage so it also survives refresh.
  const setDarkMode = useCallback((value: boolean) => {
    localStorage.setItem('theme', value ? 'dark' : 'light');
    setIsDarkMode(value);
  }, []);

  // Sync the <html> dark class so Tailwind dark: variants and token vars apply.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, setDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
